import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProgress } from '../../context/ProgressContext';
import { getAllMissions, getAvailableMissions, getMission } from '../../data/missions';
import MissionList from '../../components/mission/MissionList';
import ProgressChart from '../../components/common/ProgressChart';
import styles from './DashboardPage.module.css';
import { Mission } from '../../types/mission';
import { AgentRank, getAgentRank } from '../../types/progress';
import { getMissionRoute } from '../../utils/missionRouting';
import { formatSkillLabel } from '../../utils/missionDisplay';

/**
 * DashboardPage component
 * 
 * Displays user progress, available missions, and skill chart
 */
const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    userProgress, 
    isLoading, 
    detailedSkillDistribution, 
    weakAreas 
  } = useProgress();
  const [allMissions, setAllMissions] = useState<Mission[]>([]);
  const [availableMissions, setAvailableMissions] = useState<Mission[]>([]);
  
  // Get agent rank and statistics directly here
  const agentRank = userProgress ? getAgentRank(userProgress) : 'Rookie'; // Provide default rank
  const statistics = userProgress?.statistics;
  const skillLevels = userProgress?.skillLevels;

  useEffect(() => {
    // Load missions data
    const missions = getAllMissions();
    setAllMissions(missions);
    
    if (userProgress) {
      const available = getAvailableMissions(userProgress.completedMissions);
      setAvailableMissions(available);
    }
  }, [userProgress]);
  
  /**
   * Handle mission selection and navigate to the appropriate route
   * @param missionId The ID of the selected mission
   */
  const handleSelectMission = (missionId: string) => {
    // Get the mission to determine its type
    const mission = getMission(missionId);
    
    if (mission) {
      navigate(getMissionRoute(mission));
    } else {
      // Fallback to generic mission page if mission not found
      navigate(`/mission/${missionId}`);
    }
  };
  
  // Loading state
  if (isLoading || !userProgress || !statistics || !skillLevels) { // Added checks for potentially undefined userProgress parts
    return (
      <div className={styles.loading}>
        <p>Loading agent data...</p>
        {/* @TODO: Add asset: Loading spinner or animation */}
      </div>
    );
  }

  // Prepare skill data for the chart
  // Only use detailed skill distribution if available, otherwise use basic skill levels
  const skillChartData = detailedSkillDistribution && Object.keys(detailedSkillDistribution).length > 0
    ? detailedSkillDistribution
    : {
        coordinates: skillLevels?.coordinates || 0,
        basicMoves: skillLevels?.basicMoves || 0, 
        sequences: skillLevels?.sequences || 0,
        gameAnalysis: skillLevels?.gameAnalysis || 0
      };
  
  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Agent Dashboard</h1>
        {/* @TODO: Add asset: Intelligence agency logo */}
      </header>
      
      <div className={styles.statsRow}>
        <div className={styles.dashboardStatsContainer}>
          <div className={styles.dashboardHeader}>
            <h2 className={styles.dashboardTitle}>Agent Status Report</h2>
            <div className={styles.dashboardRank}>
              <span className={styles.dashboardRankLabel}>Rank:</span>
              <span className={styles.dashboardRankValue}>{agentRank}</span>
            </div>
          </div>
          
          <div className={styles.dashboardStatsGrid}>
            <div className={styles.dashboardStatCard}>
              <h3 className={styles.dashboardStatTitle}>Missions</h3>
              <div className={styles.dashboardStatValue}>{statistics.totalMissionsCompleted}</div>
              <div className={styles.dashboardStatLabel}>Completed</div>
            </div>
            
            <div className={styles.dashboardStatCard}>
              <h3 className={styles.dashboardStatTitle}>Accuracy</h3>
              <div className={styles.dashboardStatValue}>{statistics.averageAccuracy.toFixed(1)}%</div>
              <div className={styles.dashboardStatLabel}>Average</div>
            </div>
            
            <div className={styles.dashboardStatCard}>
              <h3 className={styles.dashboardStatTitle}>Top Score</h3>
              <div className={styles.dashboardStatValue}>{statistics.bestScore}</div>
              <div className={styles.dashboardStatLabel}>Points</div>
            </div>
            
            <div className={styles.dashboardStatCard}>
              <h3 className={styles.dashboardStatTitle}>Stage</h3>
              <div className={styles.dashboardStatValue}>{userProgress.currentStage}</div>
              <div className={styles.dashboardStatLabel}>Current</div>
            </div>
          </div>
          
          {/* Only show skill levels bars if using basic skill data */}
          {!detailedSkillDistribution || Object.keys(detailedSkillDistribution).length === 0 ? (
            <div className={styles.dashboardSkillsSection}>
              <h3 className={styles.dashboardSectionTitle}>Skill Levels</h3>
              <div className={styles.dashboardSkillsGrid}>
                {Object.entries(skillLevels)
                  .filter(([skill, level]) => typeof level === 'number') // Filter out non-numeric levels (like pieceMoves object)
                  .map(([skill, level]) => (
                  <div key={skill} className={styles.dashboardSkillItem}>
                    <div className={styles.dashboardSkillLabel}>
                      {formatSkillLabel(skill)}
                    </div>
                    <div className={styles.dashboardSkillBar}>
                      <div 
                        className={styles.dashboardSkillFill}
                        style={{ width: `${(level as number / 10) * 100}%` }}
                      />
                    </div>
                    <div className={styles.dashboardSkillValue}>Level {level}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          
          {/* Display weak areas if available */}
          {weakAreas && weakAreas.length > 0 && (
            <div className={styles.dashboardWeakAreasSection}>
              <h3 className={styles.dashboardSectionTitle}>Recommended Focus Areas</h3>
              <ul className={styles.dashboardWeakAreasList}>
                {weakAreas.slice(0, 3).map((area) => (
                  <li key={area.category} className={styles.dashboardWeakAreaItem}>
                    <span className={styles.dashboardWeakAreaCategory}>
                      {formatSkillLabel(area.category)}
                    </span>
                    <span className={styles.dashboardWeakAreaPerformance}>
                      {Math.round(area.performance)}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div className={styles.dashboardLastActive}>
            <span className={styles.dashboardLastActiveLabel}>Last Active:</span>
            <span className={styles.dashboardLastActiveValue}>
              {new Date(statistics.lastActive).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
      
      <div className={styles.contentGrid}>
        <div className={styles.missionListSection}>
          <MissionList 
            missions={allMissions} 
            userProgress={userProgress} 
            onSelectMission={handleSelectMission}
          />
        </div>
        
        <div className={styles.progressChartsSection}>
          <ProgressChart 
            skillData={skillChartData} 
            maxLevel={detailedSkillDistribution ? 100 : 10}
            title={
              detailedSkillDistribution && Object.keys(detailedSkillDistribution).length > 0 
                ? "Detailed Skill Distribution" 
                : "Skill Progress"
            }
          />
          
          <div className={styles.rankSection}>
            <h2 className={styles.rankTitle}>Promotion Requirements</h2>
            <div className={styles.rankRequirements}>
              <p className={styles.rankInfo}>
                Complete missions to increase your agent rank. Higher ranks unlock advanced missions.
              </p>
              <ul className={styles.reqList}>
                <li className={styles.reqItem}>
                  <span className={styles.reqRank}>Field Agent</span>: Complete 5 missions
                </li>
                <li className={styles.reqItem}>
                  <span className={styles.reqRank}>Special Agent</span>: Complete 15 missions
                </li>
                <li className={styles.reqItem}>
                  <span className={styles.reqRank}>Master Spy</span>: Complete all missions
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 
